import { ReactElement, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Chip,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Skeleton,
  Table,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { DownloadIcon } from '@navikt/aksel-icons';
import { downloadLogCsv, downloadLogPdf, type LogFilter, type Oversight } from '../../../api/safetyRounds';
import { useChecklistLanguage } from '../../../hooks/queries/safetyRounds/usePlanQueries';
import { useLogQuery, useOversightQuery } from '../../../hooks/queries/safetyRounds/useOversightQueries';
import { longDate, managerPlanPath, planPath } from '../Plans/plansView';
import { LOG_GROUPS, logFileName, logTime, periodError, saveFile, STATUS_COLORS } from './oversightView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Oversight.module.css';

const K = 'safetyRounds.oversight';

/** «Oversikt og logg» for HR: key figures, the status of each plan and the log, which nobody can change */
export const OversightPage = (): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useOversightQuery();

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <span className={layout.eyebrow}>{t(`${K}.eyebrow`)}</span>
          <Heading level={1} data-size="xl">
            {t(`${K}.title`)}
          </Heading>
          <Paragraph className={layout.intro}>{t(`${K}.intro`)}</Paragraph>
        </div>
      </section>
      <div className={classes.content}>
        {isLoading && (
          <div aria-busy="true">
            <span className="ds-sr-only">{t(`${K}.loading`)}</span>
            <Skeleton variant="rectangle" width="100%" height={240} />
          </div>
        )}
        {!isLoading && (error || !data) && (
          <Alert data-color="danger">
            <Heading level={2} data-size="2xs">
              {t('safetyRounds.errors.loadTitle')}
            </Heading>
            <Paragraph>{t('safetyRounds.errors.loadMessage')}</Paragraph>
          </Alert>
        )}
        {data && <Overview oversight={data} />}
        <Log />
      </div>
    </div>
  );
};

const Overview = ({ oversight }: { oversight: Oversight }): ReactElement => {
  // The general manager stays in their own part of the app
  const pathTo = useLocation().pathname.startsWith('/daglig-leder/') ? managerPlanPath : planPath;
  const { t, i18n } = useTranslation();
  const figures = [
    ['plans', oversight.keyFigures.plans],
    ['overdue', oversight.keyFigures.overdue],
    ['openDeviations', oversight.keyFigures.openDeviations],
    ['completedLastYear', oversight.keyFigures.completedLastYear],
  ] as const;
  return (
    <>
      {oversight.scope === 'ownPlans' && (
        <Alert data-color="info" data-size="sm">
          <Paragraph data-size="sm">{t(`${K}.ownPlans`)}</Paragraph>
        </Alert>
      )}
      <section aria-labelledby="oversight-figures">
        <Heading level={2} data-size="xs" id="oversight-figures" className="ds-sr-only">
          {t(`${K}.figures`)}
        </Heading>
        <dl className={classes.figures}>
          {figures.map(([key, value]) => (
            <div key={key} className={classes.figure} data-warn={key === 'overdue' && value > 0 ? true : undefined}>
              <dt>{t(`${K}.figure.${key}`)}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section aria-labelledby="oversight-plans" className={layout.card} data-gap="md">
        <Heading level={2} data-size="sm" id="oversight-plans">
          {t(`${K}.plans`)}
        </Heading>
        {oversight.plans.length === 0 ? (
          <Paragraph data-size="sm" className={layout.subtle}>
            {t(`${K}.noPlans`)}
          </Paragraph>
        ) : (
          <div className={layout.tableScroll}>
            <Table data-size="sm">
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>{t(`${K}.table.plan`)}</Table.HeaderCell>
                  <Table.HeaderCell>{t(`${K}.table.safetyRep`)}</Table.HeaderCell>
                  <Table.HeaderCell>{t(`${K}.table.next`)}</Table.HeaderCell>
                  <Table.HeaderCell>{t(`${K}.table.last`)}</Table.HeaderCell>
                  <Table.HeaderCell>{t(`${K}.table.deviations`)}</Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {oversight.plans.map((plan) => (
                  <Table.Row key={plan.id}>
                    <Table.Cell>
                      <Link asChild data-size="sm">
                        <RouterLink to={pathTo(plan.id)}>{plan.name}</RouterLink>
                      </Link>
                      <div className={layout.subtle}>{plan.area}</div>
                    </Table.Cell>
                    <Table.Cell>{plan.safetyRepEmail ?? t(`${K}.noSafetyRep`)}</Table.Cell>
                    <Table.Cell>
                      <div className={classes.next}>
                        <span>{longDate(plan.nextDate, i18n.language)}</span>
                        <Tag data-color={STATUS_COLORS[plan.status]} data-size="sm">
                          {t(`${K}.status.${plan.status}`)}
                        </Tag>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {plan.lastCompletedOn ? longDate(plan.lastCompletedOn, i18n.language) : t(`${K}.never`)}
                    </Table.Cell>
                    <Table.Cell>{plan.openDeviations}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </section>
    </>
  );
};

/** The log, with the filter HR chooses and the files to download. There is nothing to change. */
const Log = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const language = useChecklistLanguage();
  const [filter, setFilter] = useState<LogFilter>({ group: 'ALL', from: '', to: '' });
  const invalid = periodError(filter);
  // A period that ends before it starts is not asked for, so the list keeps what it had
  const [asked, setAsked] = useState(filter);
  const log = useLogQuery(asked);
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);
  const [exportFailed, setExportFailed] = useState(false);

  const change = (next: LogFilter) => {
    setFilter(next);
    if (!periodError(next)) setAsked(next);
  };

  const download = async (kind: 'csv' | 'pdf') => {
    setExporting(kind);
    setExportFailed(false);
    try {
      const blob = await (kind === 'csv' ? downloadLogCsv : downloadLogPdf)(asked, language);
      saveFile(blob, logFileName(asked, kind));
    } catch {
      setExportFailed(true);
    } finally {
      setExporting(null);
    }
  };

  const entries = log.data?.pages.flatMap((page) => page.entries) ?? [];
  const total = log.data?.pages[0]?.total ?? 0;

  return (
    <section aria-labelledby="oversight-log" className={layout.card} data-gap="md">
      <div className={layout.cardHeading}>
        <Heading level={2} data-size="sm" id="oversight-log">
          {t(`${K}.log`)}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {t(`${K}.logIntro`)}
        </Paragraph>
      </div>
      <Fieldset data-size="sm">
        <Fieldset.Legend>{t(`${K}.show`)}</Fieldset.Legend>
        <div className={classes.chips}>
          {LOG_GROUPS.map((group) => (
            <Chip.Radio
              key={group}
              name="log-group"
              checked={filter.group === group}
              onChange={() => change({ ...filter, group })}
            >
              {t(`${K}.groups.${group}`)}
            </Chip.Radio>
          ))}
        </div>
      </Fieldset>
      <div className={classes.period}>
        <Field data-size="sm">
          <Label htmlFor="log-from">{t(`${K}.from`)}</Label>
          <Input
            id="log-from"
            type="date"
            value={filter.from}
            onChange={(event) => change({ ...filter, from: event.target.value })}
          />
        </Field>
        <Field data-size="sm">
          <Label htmlFor="log-to">{t(`${K}.to`)}</Label>
          <Input
            id="log-to"
            type="date"
            value={filter.to}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? 'log-period-error' : undefined}
            onChange={(event) => change({ ...filter, to: event.target.value })}
          />
          {invalid && <ValidationMessage id="log-period-error">{t(`${K}.periodError`)}</ValidationMessage>}
        </Field>
      </div>
      <div className={classes.exports}>
        <Paragraph data-size="sm" role="status">
          {log.data ? t(`${K}.count`, { count: total }) : ''}
        </Paragraph>
        <div className={layout.actions}>
          {(['pdf', 'csv'] as const).map((kind) => (
            <Button
              key={kind}
              data-size="sm"
              variant="secondary"
              disabled={invalid || total === 0}
              aria-busy={exporting === kind || undefined}
              onClick={() => download(kind)}
            >
              <DownloadIcon aria-hidden="true" />
              {t(`${K}.download.${kind}`)}
            </Button>
          ))}
        </div>
      </div>
      {exportFailed && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t(`${K}.downloadFailed`)}</Paragraph>
        </Alert>
      )}
      {log.isLoading && <Skeleton variant="rectangle" width="100%" height={200} />}
      {log.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('safetyRounds.errors.loadMessage')}</Paragraph>
        </Alert>
      )}
      {log.data && entries.length === 0 && (
        <Paragraph data-size="sm" className={layout.subtle}>
          {t(`${K}.empty`)}
        </Paragraph>
      )}
      {entries.length > 0 && (
        <ol className={classes.entries} aria-label={t(`${K}.log`)}>
          {entries.map((entry) => (
            <li key={entry.id}>
              <span className={classes.time}>{logTime(entry.at, i18n.language)}</span>
              <span className="ds-label" data-size="sm">
                {entry.label}
                {entry.plan && <span className={layout.subtle}> · {entry.plan}</span>}
              </span>
              {entry.summary && <Paragraph data-size="sm">{entry.summary}</Paragraph>}
              <Paragraph data-size="xs" className={layout.subtle}>
                {entry.actor ?? t(`${K}.system`)}
              </Paragraph>
            </li>
          ))}
        </ol>
      )}
      {log.hasNextPage && (
        <div className={layout.actions}>
          <Button
            data-size="sm"
            variant="tertiary"
            aria-busy={log.isFetchingNextPage || undefined}
            onClick={() => log.fetchNextPage()}
          >
            {t(`${K}.more`)}
          </Button>
        </div>
      )}
    </section>
  );
};
